import { useState, useMemo, useCallback } from 'react';
import { Plus, Pencil, Lock, Package } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ProjectoPLItem, ProjectoPLItemInsert } from '../../types/projecto';
import { projectoPLService, getItemCommissionAmount, getItemEntryKind, getPassthroughSpend } from '../../services/projectoService';
import { useModal } from '../../contexts/ModalContext';
import { ProjectoPLItemModal } from './ProjectoPLItemModal';
import { PLSpreadsheetRow } from './PLSpreadsheetRow';
import { log } from '../../lib/logger';

interface Props {
  projectoId: string;
  items: ProjectoPLItem[];
  onItemsChange: (items: ProjectoPLItem[]) => void;
}

function fmt(v: number) {
  return v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

export function ProjectoPLTab({ projectoId, items, onItemsChange }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProjectoPLItem | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { showConfirm, toast } = useModal();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const revenueItems = useMemo(() => items.filter((i) => getItemEntryKind(i) === 'revenue'), [items]);
  const passthroughItems = useMemo(() => items.filter((i) => getItemEntryKind(i) === 'passthrough'), [items]);
  const absorbedItems = useMemo(() => items.filter((i) => getItemEntryKind(i) === 'cost'), [items]);

  const revenueTotal = useMemo(() => revenueItems.reduce((s, i) => s + i.total_price, 0), [revenueItems]);
  const revenueCommission = useMemo(
    () => revenueItems.reduce((s, i) => s + i.total_price * (i.margin_pct / 100), 0),
    [revenueItems]
  );
  const passthroughCharged = useMemo(
    () => passthroughItems.reduce((s, i) => s + i.total_price, 0),
    [passthroughItems]
  );
  const passthroughSpendTotal = useMemo(
    () => passthroughItems.reduce((s, i) => s + getPassthroughSpend(i), 0),
    [passthroughItems]
  );
  const passthroughCommission = Math.max(0, passthroughCharged - passthroughSpendTotal);
  const costTotal = useMemo(
    () => absorbedItems.reduce((s, i) => s + i.total_price, 0),
    [absorbedItems]
  );
  const costCommission = useMemo(
    () => absorbedItems.reduce((s, i) => s + getItemCommissionAmount(i), 0),
    [absorbedItems]
  );

  const totalChargedToClient = revenueTotal + passthroughCharged + costCommission;
  const totalCommission = revenueCommission + passthroughCommission + costCommission;
  const totalSpent = passthroughSpendTotal + costTotal;
  const netProfit = totalCommission - costTotal;

  const openAdd = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleSave = async (data: Omit<ProjectoPLItemInsert, 'projecto_id'>) => {
    try {
      if (editingItem) {
        const updated = await projectoPLService.update(editingItem.id, projectoId, data);
        onItemsChange(items.map((i) => (i.id === editingItem.id ? updated : i)));
        toast.success('Item atualizado');
      } else {
        const created = await projectoPLService.create({ ...data, projecto_id: projectoId, display_order: items.length });
        onItemsChange([...items, created]);
        toast.success('Item adicionado');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      log.error('[ProjectoPLTab] Failed to save item:', err);
      toast.error(`Erro ao guardar item: ${message}`);
      throw err; // keep the modal open so the entry is not lost
    }
  };

  const handleInlineUpdate = useCallback(async (id: string, updates: Partial<ProjectoPLItemInsert>) => {
    try {
      const updated = await projectoPLService.update(id, projectoId, updates);
      onItemsChange(items.map((i) => (i.id === id ? updated : i)));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      log.error('[ProjectoPLTab] Failed to update item:', err);
      toast.error(`Erro ao atualizar item: ${message}`);
    }
  }, [items, projectoId, onItemsChange, toast]);

  const handleDelete = async (item: ProjectoPLItem) => {
    const confirmed = await showConfirm({
      title: 'Eliminar Item?',
      message: `Remover "${item.name}" da P&L? Esta ação não pode ser revertida.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await projectoPLService.delete(item.id, projectoId);
      onItemsChange(items.filter((i) => i.id !== item.id));
      toast.success('Item eliminado');
    } catch {
      toast.error('Erro ao eliminar item');
    }
  };

  const handleDuplicate = async (item: ProjectoPLItem) => {
    try {
      const created = await projectoPLService.create({
        projecto_id: projectoId,
        item_type: item.item_type,
        entry_kind: getItemEntryKind(item),
        is_revenue: item.is_revenue,
        passthrough_breakdown: Array.isArray(item.passthrough_breakdown) ? item.passthrough_breakdown : [],
        name: `${item.name} (cópia)`,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        currency: item.currency,
        margin_pct: item.margin_pct,
        cost_commission_pct: item.cost_commission_pct,
        cost_commission_mode: item.cost_commission_mode,
        cost_commission_flat: item.cost_commission_flat,
        cost_billing_mode: item.cost_billing_mode,
        scheduled_date: item.scheduled_date,
        vendor_name: item.vendor_name,
        vendor_contact: item.vendor_contact,
        contract_ref: item.contract_ref,
        link: item.link,
        observations: item.observations,
        payment_status: item.payment_status,
        display_order: items.length,
      });
      onItemsChange([...items, created]);
      toast.success('Item duplicado');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      log.error('[ProjectoPLTab] Failed to duplicate item:', err);
      toast.error(`Erro ao duplicar item: ${message}`);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    onItemsChange(reordered);

    const orderUpdates = reordered.map((item, idx) => ({ id: item.id, display_order: idx }));
    try {
      await projectoPLService.updateOrder(orderUpdates);
    } catch {
      toast.error('Erro ao guardar ordenação');
    }
  };

  const renderSection = (
    label: string,
    sectionItems: ProjectoPLItem[],
    accentColor: 'emerald' | 'amber' | 'red',
    sectionTotal: number,
    sectionCommission: number,
  ) => {
    if (sectionItems.length === 0) return null;

    const dotColor = accentColor === 'emerald' ? 'bg-emerald-500' : accentColor === 'amber' ? 'bg-amber-500' : 'bg-red-400';
    const totalColor = accentColor === 'emerald' ? 'text-emerald-700' : accentColor === 'amber' ? 'text-amber-700' : 'text-red-600';
    const colSpanBefore = isEditing ? 5 : 4;

    return (
      <>
        <tr className="bg-gray-50/60">
          <td colSpan={isEditing ? 9 : 8} className="px-3 py-2">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${dotColor}`} />
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">{label}</span>
              <span className="text-[10px] text-gray-400">({sectionItems.length})</span>
            </div>
          </td>
        </tr>
        {sectionItems.map((item) => (
          <PLSpreadsheetRow
            key={item.id}
            item={item}
            isEditing={isEditing}
            onInlineUpdate={handleInlineUpdate}
            onEditDetail={(it) => { setEditingItem(it); setModalOpen(true); }}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
        ))}
        <tr className="border-t border-gray-200 bg-gray-50/40">
          <td colSpan={colSpanBefore} className="px-3 py-1.5 text-right">
            <span className="text-[11px] font-semibold text-gray-500 uppercase">Subtotal</span>
          </td>
          <td className="px-2 py-1.5 text-right">
            <span className={`text-sm font-bold tabular-nums ${totalColor}`}>{fmt(sectionTotal)}</span>
          </td>
          <td className="px-2 py-1.5 text-right">
            <span className="text-sm font-bold tabular-nums text-emerald-700">{fmt(sectionCommission)}</span>
          </td>
          <td colSpan={2} />
        </tr>
      </>
    );
  };

  return (
    <div className="space-y-5 pb-24">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
              <Pencil className="w-3 h-3" />
              Modo de edição
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              <Lock className="w-3 h-3" />
              Bloqueado
            </span>
          )}
          <span className="text-xs text-gray-400">
            {isEditing ? 'Duplo-clique nas células para editar. Arraste linhas para reordenar.' : 'Clique Editar para modificar a P&L.'}
          </span>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            isEditing
              ? 'bg-accent-600 text-white hover:bg-accent-700 shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {isEditing ? (
            <>
              <Lock className="w-3.5 h-3.5" />
              Bloquear
            </>
          ) : (
            <>
              <Pencil className="w-3.5 h-3.5" />
              Editar
            </>
          )}
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50 border-gray-200">
            <div className="flex items-center gap-2 text-gray-700">
              <Package className="w-4 h-4" />
              <span className="text-sm font-semibold">Itens & Serviços</span>
              <span className="text-xs font-medium opacity-60">({items.length} itens)</span>
            </div>
            <div className="flex items-center gap-3">
              {isEditing && (
                <button
                  type="button"
                  onClick={openAdd}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors bg-accent-600 hover:bg-accent-700 text-white"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Adicionar Item
                </button>
              )}
            </div>
          </div>

          {items.length === 0 && !isEditing ? (
            <div className="py-10 text-center text-sm text-gray-400">
              <p>Sem itens.</p>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="mt-2 text-blue-600 hover:underline text-xs font-medium"
              >
                Mudar para modo de edição para adicionar itens
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    {isEditing && <th className="w-8 px-1 py-2" />}
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-16">Tipo</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider min-w-[140px]">Nome</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-14 text-right">Qtd</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-24 text-right">Preço Unit.</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-24 text-right">Total</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-24 text-right">Comissão</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-24">Estado</th>
                    <th className="px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-20 text-right">Ações</th>
                  </tr>
                </thead>
                <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {renderSection('Serviços Próprios', revenueItems, 'emerald', revenueTotal, revenueCommission)}
                    {renderSection('Pass-through', passthroughItems, 'amber', passthroughCharged, passthroughCommission)}
                    {renderSection('Custos Absorvidos', absorbedItems, 'red', costTotal, costCommission)}
                  </tbody>
                </SortableContext>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-white">
                    {isEditing && <td />}
                    <td colSpan={4} className="px-3 py-3 text-right">
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Total Faturado ao Cliente</span>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <span className="text-base font-bold text-gray-900 tabular-nums">{fmt(totalChargedToClient)}</span>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <span className="text-base font-bold text-emerald-700 tabular-nums">{fmt(totalCommission)}</span>
                    </td>
                    <td colSpan={2} />
                  </tr>
                  {totalSpent > 0 && (
                    <tr className="bg-white border-t border-gray-100">
                      {isEditing && <td />}
                      <td colSpan={4} className="px-3 py-2 text-right">
                        <span className="text-[11px] font-semibold text-gray-500 uppercase">Total Gasto (fornecedores)</span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-sm font-semibold text-red-600 tabular-nums">({fmt(totalSpent)})</span>
                      </td>
                      <td colSpan={3} />
                    </tr>
                  )}
                  <tr className="bg-gray-50/80 border-t-2 border-gray-200">
                    {isEditing && <td />}
                    <td colSpan={4} className="px-3 py-3 text-right">
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Lucro Líquido</span>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <span className={`text-base font-bold tabular-nums ${netProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {fmt(netProfit)}
                      </span>
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </DndContext>

      {modalOpen && (
        <ProjectoPLItemModal
          item={editingItem}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingItem(null); }}
        />
      )}
    </div>
  );
}
